import React, { useEffect, useState, useCallback } from "react";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { useCustomEventListener } from "react-custom-events";
import { useSelector } from "react-redux";
import { selectRoles } from "../../store/authSlice";
import AddLocationModal from "../../components/molecules/FormLocation";
import LocationApi from "../../api/Location";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loading from "../../components/Loading";
import { DashboardLayout } from "../../components/organisms/Layout";
import { FormModal } from "../../components/organisms/FormModal";
import DeleteModal from "../../components/modals/DeleteModal";
import PaginationView from "../../components/molecules/Pagination";
import { encodeIds } from "../../utils";
import SearchBar from "../../components/atoms/SearchBar";
import useQuery from "../../hooks/useQuery";
import Table from "../../components/atoms/Tables/Table";
import { addIcon } from "../../assets/images";

const Locations = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useQuery();
  const roles = useSelector(selectRoles);
  const [showModal, setShowModal] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metaData, setMetaData] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchLocation, setSearchLocation] = useState("");
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [pageLimit, setPagelimit] = useState(query.get("pageSize") || 50);
  const isSuperUser = false;

  useEffect(() => {
    document.title = "HeyHire Portal-Locations";
  }, []);

  useEffect(() => {
    loadLocations();
  }, [query]);

  useCustomEventListener("closeModal", () => {
    setShowModal(false);
    setLocationToDelete(null);
  });

  useCustomEventListener("dataChanged", () => {
    loadLocations();
  });

  const debouncedUpdateSearch = useCallback(
    debounce((txt) => {
      // Create a new object from current search params
      const newParams = { ...Object.fromEntries(searchParams) };
      
      // Reset to page 1 when searching
      newParams.page = 1;
      
      // Add or remove search parameter based on value
      if (txt && txt.trim() !== '') {
        newParams.search = txt.trim();
      } else {
        delete newParams.search;
      }
      
      setSearchParams(newParams);
    }, 300),
    [searchParams]
  );

  const searchLocationName = (txt) => {
    setSearchLocation(txt);
    debouncedUpdateSearch(txt);
  };

  const handlecPageChange = (currentPage, pageSize) => {
    setPagelimit(pageSize);
    // Create a new object from current search params
    const newParams = { ...Object.fromEntries(searchParams) };
    
    newParams.page = currentPage;
    newParams.limit = pageSize;
    
    // Only include search if it has a value
    if (searchLocation && searchLocation.trim() !== '') {
      newParams.search = searchLocation.trim();
    } else {
      delete newParams.search;
    }
    
    setSearchParams(newParams);
  };

  // const addLocation = (values) => {
  //   setLoading(true);

  //   Location.add(values)
  //     .then(() => {
  //       toast.success(t("locations.location-added"));
  //     })
  //     .catch((error) => {
  //       if (error.response && error.response.status === 422) {
  //         // Validation error
  //       } else {
  //         if (error?.response?.status !== 403) {
  //           toast.error(t("common.something-went-wrong-please-try-again"));
  //         }
  //       }
  //     })
  //     .then(() => setLoading(false));
  // };

  const loadLocations = () => {
    setLoading(true);
    const page = query.get("page") || 1;
    const limit = query.get("limit") || pageLimit;
    const search = query.get("search") || "";
    
    const pageData = page ? `page=${page}` : "";
    const searchData = search ? `&search=${encodeURIComponent(search)}` : "";
    const queryData = pageData + `&limit=${limit}` + searchData;
    
    // Update the search input field when loading from URL
    setSearchLocation(search);
    
    LocationApi.getAll(queryData)
      .then((response) => {
        setLocations(response.data.data);
        setFilteredLocations(response.data.data);
        setMetaData(response.data.meta);
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error(t("common.something-went-wrong"));
        }
      })
      .then(() => setLoading(false));
  };

  const onLocationDeleted = () => {
    setLocationToDelete(null);
    loadLocations();
  };

  return (
    <DashboardLayout darmo={true}>
      <div className="pb-12">
        <Table
          isLoading={loading}
          mainHeader="Locations"
          searchBar={
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full sm:justify-end">
              <div className="w-full sm:w-[500px]">
                <SearchBar
                  searchValue={searchLocation}
                  placeholder="Search by location name, address, category or region..."
                  searchFunction={(value) => searchLocationName(value)}
                />
              </div>
            </div>
          }
          maxHeight="calc(100vh - 250px)"
          headers={[
            "Name",
            isSuperUser ? "Company Name" : "Address",
            "Category",
            "Region",
            "Edit",
            "Delete"
          ]}
          data={
            loading ? [] :
            filteredLocations?.map((location, index) => (
              <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                <td className="whitespace-nowrap px-6 py-4 text-sm">
                  <div className="font-semibold text-gray-900">
                    {location.name}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm">
                  <div className="text-gray-700">
                    {isSuperUser
                      ? location?.company?.name
                      : location?.address?.address}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm">
                  <div className="text-gray-700">
                    {(() => {
                      // Handle category if it's an array
                      if (Array.isArray(location.category) && location.category.length > 0) {
                        return location.category[0].name;
                      }
                      // Fallback for object case
                      return location.category?.name || '';
                    })()}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm">
                  <div className="text-gray-700">
                    {location.region && location.region.name}
                  </div>
                </td>
                <td className="whitespace-nowrap px-2 sm:px-3 py-2.5 sm:py-3 text-sm md:text-base">
                  <div className="flex items-center gap-4">
                    <button
                      className="text-primary-600 hover:text-primary-900 font-medium hover:underline"
                      onClick={() =>
                        navigate(
                          `/companies/locations/${encodeIds(
                            location.id
                          )}/edit`
                        )
                      }
                    >
                      Edit
                    </button>
                  </div>
                </td>
                <td className="whitespace-nowrap px-2 sm:px-3 py-2.5 sm:py-3 text-sm md:text-base">
                  <div className="flex items-center gap-4">
                    <button
                      className="text-primary-600 hover:text-primary-900 font-medium hover:underline"
                      onClick={() => {
                        setLocationToDelete(location.id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))
          }
          buttonData={
            <>
              {roles[0] === "super-user" && (
                <button
                  onClick={() => navigate("add")}
                  className="w-full sm:w-auto flex-shrink-0 cursor-pointer flex justify-center items-center rounded-lg bg-primary-500 px-6 h-10 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 transition-all duration-200"
                >
                  <img src={addIcon} alt="add" className="mr-2 w-4 h-4" />
                  Add Location
                </button>
              )}
            </>
          }
        />
        <div className="flex w-full h-20 justify-center md:justify-end items-center px-4">
          {!loading && metaData?.total > 0 && (
            <PaginationView
              current={metaData?.current_page}
              next={metaData?.to}
              pageSize={metaData?.per_page}
              total={metaData?.total}
              onChange={handlecPageChange}
            />
          )}
        </div>
      </div>
      <FormModal
        visible={showModal}
        onClose={() => setShowModal(false)}
        title={t("locations.add-a-location")}
        subTitle="Vivamus venenatis eros sit amet lectus gravida, volutpat dictum ex pellentesque. "
        // onSubmit={addLocation}
      >
        <AddLocationModal />
      </FormModal>

      {locationToDelete && (
        <DeleteModal
          title="Delete Location"
          name="Location"
          deleteRequest={() => LocationApi.delete(locationToDelete)}
          onSuccess={onLocationDeleted}
        />
      )}
    </DashboardLayout>
  );
};

export default Locations;
